'use client'

import { useState, useEffect } from 'react'
import { useRouter } from '@/i18n/navigation'
import { useTranslations } from 'next-intl'
import type { Category } from '@/payload-types'

interface SearchAndFilterProps {
  categories: Category[]
  currentCategory?: string
  currentSearch?: string
}

export function SearchAndFilter({
  categories,
  currentCategory = '',
  currentSearch = '',
}: SearchAndFilterProps) {
  const [selectedCategory, setSelectedCategory] = useState(currentCategory)
  const [searchTerm, setSearchTerm] = useState(currentSearch)
  const [inputValue, setInputValue] = useState(currentSearch)
  const router = useRouter()
  const t = useTranslations('Blog')

  useEffect(() => {
    setSelectedCategory(currentCategory)
    setSearchTerm(currentSearch)
    setInputValue(currentSearch)
  }, [currentCategory, currentSearch])

  const buildUrlPath = (search?: string, category?: string) => {
    const params: string[] = []

    if (search) {
      params.push('search', encodeURIComponent(search))
    }

    if (category) {
      params.push('category', encodeURIComponent(category))
    }

    return params.length > 0 ? `/blog/${params.join('/')}` : '/blog'
  }

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category)
    const newPath = buildUrlPath(searchTerm, category)
    router.push(newPath)
  }

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSearchTerm(inputValue)
    const newPath = buildUrlPath(inputValue, selectedCategory)
    router.push(newPath)
  }

  const handleReset = () => {
    setSelectedCategory('')
    setSearchTerm('')
    setInputValue('')
    router.push('/blog')
  }

  return (
    <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-8 space-y-8">
      {/* Search */}
      <div>
        <h3 className="text-xl font-semibold mb-4 text-transparent bg-gradient-to-r from-strandforge-purple to-strandforge-purple-light bg-clip-text">
          {t('search')}
        </h3>
        <form onSubmit={handleSearchSubmit} className="space-y-4">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="w-full px-4 py-3 bg-black/50 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-strandforge-purple/50 focus:ring-1 focus:ring-strandforge-purple/50 transition-all duration-300"
          />
          <button
            type="submit"
            className="w-full bg-gradient-to-r from-strandforge-purple to-strandforge-purple-light hover:from-strandforge-purple-dark hover:to-strandforge-purple text-white font-medium py-3 px-4 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-strandforge-purple/30"
          >
            {t('searchButton')}
          </button>
        </form>
      </div>

      {/* Categories */}
      <div>
        <h3 className="text-xl font-semibold mb-4 text-transparent bg-gradient-to-r from-strandforge-purple to-strandforge-purple-light bg-clip-text">
          {t('categories')}
        </h3>
        <div className="space-y-3">
          <button
            onClick={() => handleCategoryChange('')}
            className={`block w-full text-left px-4 py-3 rounded-xl transition-all duration-300 ${
              !selectedCategory
                ? 'bg-gradient-to-r from-strandforge-purple to-strandforge-purple-light text-white shadow-lg shadow-strandforge-purple/30'
                : 'text-gray-300 hover:text-white hover:bg-white/10 border border-transparent hover:border-white/20'
            }`}
          >
            {t('allCategories')}
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategoryChange(category.slug || '')}
              className={`block w-full text-left px-4 py-3 rounded-xl transition-all duration-300 ${
                selectedCategory === category.slug
                  ? 'bg-gradient-to-r from-strandforge-purple to-strandforge-purple-light text-white shadow-lg shadow-strandforge-purple/30'
                  : 'text-gray-300 hover:text-white hover:bg-white/10 border border-transparent hover:border-white/20'
              }`}
            >
              {category.title}
            </button>
          ))}
        </div>
      </div>

      {/* Reset button */}
      {(selectedCategory || searchTerm) && (
        <button
          onClick={handleReset}
          className="w-full bg-black/50 hover:bg-white/10 border border-white/20 hover:border-white/40 text-white font-medium py-3 px-4 rounded-xl transition-all duration-300"
        >
          {t('clearFilters')}
        </button>
      )}
    </div>
  )
}
